"use client";

import React, { useRef, useState, useCallback } from 'react';
import styles from './_bsa-step-inputs.module.scss';
import { FormInputText } from '@/components/form';
import Button from '@/components/button';
import {
  BSA_UNIT_HEIGHT,
  BSA_UNIT_WEIGHT,
  toWeightKg,
  computeBSA,
} from '@/data/bsa-calculator-data';

const HEIGHT_IN_MAX  = 120; // 10 ft in inches
const HEIGHT_IN_MIN  = 12;  // ~1 ft, reasonable lower bound
const HEIGHT_CM_MAX  = 305;
const HEIGHT_CM_MIN  = 30;
const WEIGHT_LBS_MAX = 999;
const WEIGHT_LBS_MIN = 1;
const WEIGHT_KG_MAX  = 453;
const WEIGHT_KG_MIN  = 1;

function isPositiveNumeric(val) {
  const n = parseFloat(val);
  return !isNaN(n) && n > 0;
}

/**
 * @description BSA Calculator — Step: Patient Measurements
 * Collects height (ft + in or cm) and weight (lbs or kg) and displays
 * the computed BSA using the Mosteller formula.
 *
 * @param {object}   formValues  — current field values from parent state
 * @param {Function} onNext      — callback when Calculate is clicked
 * @param {Function} onReset     — callback to reset the calculator
 */
function BsaStepInputs({ formValues, onNext, onReset }) {
  const heightUnitRef = useRef(formValues.heightUnit ?? BSA_UNIT_HEIGHT.IMPERIAL);
  const weightUnitRef = useRef(formValues.weightUnit ?? BSA_UNIT_WEIGHT.IMPERIAL);

  const heightInRef  = useRef(null);
  const heightCmRef  = useRef(null);
  const weightLbsRef = useRef(null);
  const weightKgRef  = useRef(null);

  const [heightUnit, setHeightUnit] = useState(heightUnitRef.current);
  const [weightUnit, setWeightUnit] = useState(weightUnitRef.current);

  const [bsaResult, setBsaResult] = useState(formValues.bsaResult ?? null);
  const [isCalculateDisabled, setIsCalculateDisabled] = useState(true);

  const [localHeightIn,  setLocalHeightIn]  = useState(formValues.heightIn  ?? '');
  const [localHeightCm,  setLocalHeightCm]  = useState(formValues.heightCm  ?? '');
  const [localWeightLbs, setLocalWeightLbs] = useState(formValues.weightLbs ?? '');
  const [localWeightKg,  setLocalWeightKg]  = useState(formValues.weightKg  ?? '');

  const validateInputs = useCallback(() => {
    const heightOk = heightUnit === BSA_UNIT_HEIGHT.IMPERIAL
      ? isPositiveNumeric(heightInRef.current?.value)
      : isPositiveNumeric(heightCmRef.current?.value);

    const weightOk = weightUnit === BSA_UNIT_WEIGHT.IMPERIAL
      ? isPositiveNumeric(weightLbsRef.current?.value)
      : isPositiveNumeric(weightKgRef.current?.value);

    setIsCalculateDisabled(!(heightOk && weightOk));
  }, [heightUnit, weightUnit]);

  const handleCalculate = useCallback(() => {
    const heightInVal  = heightInRef.current?.value  ?? '';
    const heightCmVal  = heightCmRef.current?.value  ?? '';
    const weightLbsVal = weightLbsRef.current?.value ?? '';
    const weightKgVal  = weightKgRef.current?.value  ?? '';

    const heightCm = heightUnit === BSA_UNIT_HEIGHT.IMPERIAL
      ? parseFloat(heightInVal) * 2.54
      : parseFloat(heightCmVal);

    const weightKg = weightUnit === BSA_UNIT_WEIGHT.IMPERIAL
      ? toWeightKg(weightLbsVal)
      : parseFloat(weightKgVal);

    const bsa = computeBSA({ heightCm, weightKg });
    setBsaResult(bsa);

    onNext({
      heightIn:   heightInVal,
      heightCm:   heightCmVal,
      weightLbs:  weightLbsVal,
      weightKg:   weightKgVal,
      heightUnit: heightUnit,
      weightUnit: weightUnit,
      bsaResult:  bsa,
    });
  }, [heightUnit, weightUnit, onNext]);

  return (
    <div className={styles.bsa_step_inputs}>
      <span className={styles.section_title}>Patient&rsquo;s height and weight</span>

      {/* ── Height ───────────────────────────────────────────────────── */}
      <div className={styles.input_row}>
        <div className={styles.input_left}>
          <span className={styles.field_label}>Height</span>
          {heightUnit === BSA_UNIT_HEIGHT.IMPERIAL ? (
            <FormInputText
              key="height-imperial"
              ref={heightInRef}
              name="bsa-height"
              label=""
              ariaLabel="Height in inches"
              type="text"
              min={HEIGHT_IN_MIN}
              max={HEIGHT_IN_MAX}
              defaultValue={localHeightIn}
              onChange={() => { setBsaResult(null); validateInputs(); }}
              onValidate={validateInputs}
              inputMode="decimal"
              autoformatDecimalOnly
              maxLength={6}
            />
          ) : (
            <FormInputText
              key="height-metric"
              ref={heightCmRef}
              name="bsa-height"
              label=""
              ariaLabel="Height in centimeters"
              type="text"
              min={HEIGHT_CM_MIN}
              max={HEIGHT_CM_MAX}
              defaultValue={localHeightCm}
              onChange={() => { setBsaResult(null); validateInputs(); }}
              onValidate={validateInputs}
              inputMode="decimal"
              autoformatDecimalOnly
              maxLength={6}
            />
          )}
        </div>
        <div className={styles.input_right}>
          <div className={styles.unit_radio_group}>
            <label className={styles.unit_radio_option}>
              <input
                type="radio"
                name="bsa-height-unit"
                value={BSA_UNIT_HEIGHT.METRIC}
                checked={heightUnit === BSA_UNIT_HEIGHT.METRIC}
                onChange={() => {
                  const inVal = heightInRef.current?.value ?? '';
                  const n = parseFloat(inVal);
                  const newCm = (!isNaN(n) && n > 0) ? parseFloat((n * 2.54).toFixed(2)).toString() : '';
                  setLocalHeightCm(newCm);
                  const heightOk = newCm !== '';
                  const weightOk = weightUnitRef.current === BSA_UNIT_WEIGHT.IMPERIAL
                    ? isPositiveNumeric(weightLbsRef.current?.value)
                    : isPositiveNumeric(weightKgRef.current?.value);
                  setIsCalculateDisabled(!(heightOk && weightOk));
                  heightUnitRef.current = BSA_UNIT_HEIGHT.METRIC;
                  setHeightUnit(BSA_UNIT_HEIGHT.METRIC);
                  setBsaResult(null);
                }}
                data-gtm-event-category="bsa-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Centimeters"
              />
              Centimeters
            </label>
            <label className={styles.unit_radio_option}>
              <input
                type="radio"
                name="bsa-height-unit"
                value={BSA_UNIT_HEIGHT.IMPERIAL}
                checked={heightUnit === BSA_UNIT_HEIGHT.IMPERIAL}
                onChange={() => {
                  const cmVal = heightCmRef.current?.value ?? '';
                  const n = parseFloat(cmVal);
                  const newIn = (!isNaN(n) && n > 0) ? parseFloat((n / 2.54).toFixed(2)).toString() : '';
                  setLocalHeightIn(newIn);
                  const heightOk = newIn !== '';
                  const weightOk = weightUnitRef.current === BSA_UNIT_WEIGHT.IMPERIAL
                    ? isPositiveNumeric(weightLbsRef.current?.value)
                    : isPositiveNumeric(weightKgRef.current?.value);
                  setIsCalculateDisabled(!(heightOk && weightOk));
                  heightUnitRef.current = BSA_UNIT_HEIGHT.IMPERIAL;
                  setHeightUnit(BSA_UNIT_HEIGHT.IMPERIAL);
                  setBsaResult(null);
                }}
                data-gtm-event-category="bsa-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Inches"
              />
              Inches
            </label>
          </div>
        </div>
      </div>

      {/* ── Weight ───────────────────────────────────────────────────── */}
      <div className={styles.input_row}>
        <div className={styles.input_left}>
          <span className={styles.field_label}>Weight</span>
          {weightUnit === BSA_UNIT_WEIGHT.IMPERIAL ? (
            <FormInputText
              key="weight-imperial"
              ref={weightLbsRef}
              name="bsa-weight"
              label=""
              ariaLabel="Weight in pounds"
              type="text"
              min={WEIGHT_LBS_MIN}
              max={WEIGHT_LBS_MAX}
              defaultValue={localWeightLbs}
              onChange={() => { setBsaResult(null); validateInputs(); }}
              onValidate={validateInputs}
              inputMode="decimal"
              autoformatDecimalOnly
              maxLength={6}
            />
          ) : (
            <FormInputText
              key="weight-metric"
              ref={weightKgRef}
              name="bsa-weight"
              label=""
              ariaLabel="Weight in kilograms"
              type="text"
              min={WEIGHT_KG_MIN}
              max={WEIGHT_KG_MAX}
              defaultValue={localWeightKg}
              onChange={() => { setBsaResult(null); validateInputs(); }}
              onValidate={validateInputs}
              inputMode="decimal"
              autoformatDecimalOnly
              maxLength={6}
            />
          )}
        </div>
        <div className={styles.input_right}>
          <div className={styles.unit_radio_group}>
            <label className={styles.unit_radio_option}>
              <input
                type="radio"
                name="bsa-weight-unit"
                value={BSA_UNIT_WEIGHT.METRIC}
                checked={weightUnit === BSA_UNIT_WEIGHT.METRIC}
                onChange={() => {
                  const lbsVal = weightLbsRef.current?.value ?? '';
                  const n = parseFloat(lbsVal);
                  const newKg = (!isNaN(n) && n > 0) ? parseFloat(toWeightKg(lbsVal).toFixed(2)).toString() : '';
                  setLocalWeightKg(newKg);
                  const weightOk = newKg !== '';
                  const heightOk = heightUnitRef.current === BSA_UNIT_HEIGHT.IMPERIAL
                    ? isPositiveNumeric(heightInRef.current?.value)
                    : isPositiveNumeric(heightCmRef.current?.value);
                  setIsCalculateDisabled(!(heightOk && weightOk));
                  weightUnitRef.current = BSA_UNIT_WEIGHT.METRIC;
                  setWeightUnit(BSA_UNIT_WEIGHT.METRIC);
                  setBsaResult(null);
                }}
                data-gtm-event-category="bsa-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Weight Unit: kg"
              />
              Kilograms
            </label>
            <label className={styles.unit_radio_option}>
              <input
                type="radio"
                name="bsa-weight-unit"
                value={BSA_UNIT_WEIGHT.IMPERIAL}
                checked={weightUnit === BSA_UNIT_WEIGHT.IMPERIAL}
                onChange={() => {
                  const kgVal = weightKgRef.current?.value ?? '';
                  const n = parseFloat(kgVal);
                  const newLbs = (!isNaN(n) && n > 0) ? parseFloat((n * 2.2046).toFixed(2)).toString() : '';
                  setLocalWeightLbs(newLbs);
                  const weightOk = newLbs !== '';
                  const heightOk = heightUnitRef.current === BSA_UNIT_HEIGHT.IMPERIAL
                    ? isPositiveNumeric(heightInRef.current?.value)
                    : isPositiveNumeric(heightCmRef.current?.value);
                  setIsCalculateDisabled(!(heightOk && weightOk));
                  weightUnitRef.current = BSA_UNIT_WEIGHT.IMPERIAL;
                  setWeightUnit(BSA_UNIT_WEIGHT.IMPERIAL);
                  setBsaResult(null);
                }}
                data-gtm-event-category="bsa-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Weight Unit: lbs"
              />
              Pounds
            </label>
          </div>
        </div>
      </div>

      {/* ── Result ───────────────────────────────────────────────────── */}
      {bsaResult !== null && (
        <div className={styles.result} aria-live="polite">
          <span className={styles.result_label}>Body surface area (m<sup>2</sup>):</span>
          <div className={styles.result_value_box}>
            <span className={styles.result_value}>{bsaResult.toFixed(2)} m&sup2;</span>
          </div>
          <p className={styles.result_formula}>
            Calculated using the DuBois formula: Body surface area = (W<sup>0.425</sup> &times; H<sup>0.725</sup>) &times; 0.007184 where weight is in kilograms and height is in centimeters. DuBois formula was used in CRENESSITY trials.
          </p>
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      {bsaResult === null && (
        <div className={styles.actions}>
          <Button
            iconType="arrow-right"
            type="button"
            variant={['solid', 'solid--eggplant', 'hcp-big']}
            disabled={isCalculateDisabled}
            onClick={handleCalculate}
            data-gtm-event-category="bsa-calculator"
            data-gtm-event-action="Click"
            data-gtm-event-label="Calculate"
          >
            Calculate
          </Button>
        </div>
      )}
    </div>  
  );
}

export default BsaStepInputs;
